import { useEffect, useMemo, useRef, useState } from "react";
import { Check, ChevronDown, Lightbulb, X } from "lucide-react";
import { useIdeaList } from "@/modules/ideas/hooks/useIdeas";

const BORDER = "hsl(210,30%,85%)";

type IdeaLite = { id: string; code: string | null; title: string };

type Props = {
  /** Currently selected Idea ids. */
  selectedIds: string[];
  /** Called with the next full set of selected ids on every add/remove. */
  onChange: (ids: string[]) => void;
  /**
   * Ideas already known to the caller (e.g. a decision's currently-linked
   * ideas) so their chips render with code + title before any search runs.
   */
  knownIdeas?: IdeaLite[];
};

/**
 * Searchable multi-select for linking Ideas to a Decision. Supports 0, 1, or
 * many selections. Mirrors the drawer's select styling and the tag chip
 * pattern used elsewhere in the Decisions module.
 */
export function IdeasMultiSelect({
  selectedIds,
  onChange,
  knownIdeas = [],
}: Props) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const containerRef = useRef<HTMLDivElement>(null);

  const { data: ideasResult } = useIdeaList({
    page: 1,
    limit: 100,
    search: search || undefined,
  });
  const ideas = ideasResult?.data ?? [];

  // Resolve chip labels from caller-supplied ideas plus everything we have
  // seen in fetched lists, falling back to the raw id if still unknown.
  const labelMap = useMemo(() => {
    const m = new Map<string, IdeaLite>();
    for (const i of knownIdeas) m.set(i.id, i);
    for (const i of ideas)
      m.set(i.id, { id: i.id, code: i.code, title: i.title });
    return m;
  }, [knownIdeas, ideas]);

  useEffect(() => {
    if (!open) return;
    function onDocClick(e: MouseEvent) {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [open]);

  function toggle(id: string) {
    if (selectedIds.includes(id)) {
      onChange(selectedIds.filter((x) => x !== id));
    } else {
      onChange([...selectedIds, id]);
    }
  }

  function chipLabel(id: string): string {
    const i = labelMap.get(id);
    if (!i) return id;
    return i.code ? `${i.code} · ${i.title}` : i.title;
  }

  return (
    <div ref={containerRef} className="relative space-y-2">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex h-9 w-full items-center justify-between rounded-md border border-border bg-background px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        <span
          className={
            selectedIds.length > 0
              ? "text-foreground"
              : "text-muted-foreground"
          }
        >
          {selectedIds.length > 0
            ? `${selectedIds.length} idea${selectedIds.length === 1 ? "" : "s"} selected`
            : "Link ideas…"}
        </span>
        <ChevronDown
          className="h-4 w-4 text-muted-foreground"
          strokeWidth={1.5}
        />
      </button>

      {selectedIds.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {selectedIds.map((id) => (
            <span
              key={id}
              className="inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-medium"
              style={{
                borderColor: "hsl(40,85%,80%)",
                background: "hsl(40,95%,95%)",
                color: "hsl(35,85%,32%)",
              }}
            >
              <Lightbulb className="h-3 w-3" strokeWidth={1.5} />
              <span className="max-w-[220px] truncate">{chipLabel(id)}</span>
              <button
                type="button"
                onClick={() => toggle(id)}
                className="ml-0.5 text-current opacity-70 hover:opacity-100"
                title="Remove"
              >
                <X className="h-3 w-3" strokeWidth={2} />
              </button>
            </span>
          ))}
        </div>
      )}

      {open && (
        <div
          className="absolute z-[10000] mt-1 w-full rounded-md border bg-white p-2 shadow-xl"
          style={{ borderColor: BORDER }}
        >
          <input
            autoFocus
            placeholder="Search ideas…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="mb-2 h-8 w-full rounded-md border border-border bg-background px-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          />
          <div className="max-h-[260px] space-y-1 overflow-y-auto">
            {ideas.length === 0 ? (
              <div className="py-5 text-center text-xs text-muted-foreground">
                No ideas found.
              </div>
            ) : (
              ideas.map((idea) => {
                const selected = selectedIds.includes(idea.id);
                return (
                  <button
                    key={idea.id}
                    type="button"
                    onClick={() => toggle(idea.id)}
                    className="flex w-full items-center justify-between gap-3 rounded-md border px-3 py-2 text-left hover:bg-[hsl(210,50%,98%)]"
                    style={{
                      borderColor: selected ? "hsl(40,85%,75%)" : BORDER,
                      background: selected ? "hsl(40,95%,97%)" : "white",
                    }}
                  >
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 text-[11px] text-muted-foreground">
                        <span className="font-mono">{idea.code ?? "—"}</span>
                        <span className="capitalize">{idea.status}</span>
                      </div>
                      <div className="truncate text-[13px] font-medium">
                        {idea.title}
                      </div>
                    </div>
                    {selected && (
                      <Check
                        className="h-4 w-4 shrink-0 text-[hsl(35,85%,40%)]"
                        strokeWidth={2}
                      />
                    )}
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}
